import React, { useMemo, useState } from 'react';
import { inject, observer } from 'mobx-react';
import TailwindScope from '../Shared/TailwindScope';
import { callApi } from '../../util/apiWrapper';
import '../../tailwind.css';

interface AuthUser {
  first_name?: string;
  last_name?: string;
  email?: string;
  roles?: string[];
}

interface ImpersonationAuthStore {
  isImpersonating: boolean;
  impersonatedUser: AuthUser | null;
  handleImpersonationEnd: (response?: unknown) => void;
}

interface SeniorStoreShape {
  resetState?: () => void;
}

interface ImpersonationBannerProps {
  AuthStore?: ImpersonationAuthStore;
  SeniorStore?: SeniorStoreShape;
}

export const ImpersonationBannerBase = ({ AuthStore: authStore, SeniorStore: seniorStore }: ImpersonationBannerProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const impersonatedUser = authStore?.impersonatedUser;
  const isVisible = Boolean(authStore?.isImpersonating && impersonatedUser);
  const fullName = useMemo(() => {
    if (!impersonatedUser) {
      return '';
    }
    return [impersonatedUser.first_name, impersonatedUser.last_name].filter(Boolean).join(' ');
  }, [impersonatedUser]);

  if (!isVisible || !authStore || !impersonatedUser) {
    return null;
  }

  const handleEndImpersonation = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      const response = await callApi(authStore, '/user/impersonation', 'DELETE');
      authStore.handleImpersonationEnd(response);
      seniorStore?.resetState?.();
      // return to home
      window.location.href = '/home';
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Unable to end impersonation.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <TailwindScope>
      <div
        role="status"
        aria-live="polite"
        className="fixed bottom-4 left-1/2 z-[1300] w-[50%] max-w-5xl -translate-x-1/2 rounded-lg border border-purple bg-purple-tint px-4 py-3 shadow-lg"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-4">
          <div className="min-w-0 flex-1 text-left">
            <p className="m-0 text-sm font-bold text-purple">Impersonating user</p>
            <p className="m-0 break-words text-sm text-brand-navy">
              {fullName || 'Unknown user'} ({impersonatedUser.email || 'No email'})
            </p>
            <p className="m-0 break-words text-xs text-gray-700">Roles: {(impersonatedUser.roles || []).join(', ') || 'None'}</p>
            {errorMessage && <p className="m-0 mt-1 text-xs text-error">{errorMessage}</p>}
          </div>
          <button
            type="button"
            aria-label="End impersonation"
            className="w-full shrink-0 self-auto rounded-full border-none bg-purple px-4 py-2 text-xs font-bold uppercase text-white sm:w-auto sm:self-center"
            onClick={handleEndImpersonation}
            disabled={isLoading}
          >
            {isLoading ? 'Ending...' : 'End Impersonation'}
          </button>
        </div>
      </div>
    </TailwindScope>
  );
};

const ImpersonationBanner = inject('AuthStore', 'SeniorStore')(observer(ImpersonationBannerBase));

export default ImpersonationBanner;
